/* @refresh reset */
import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS, TYPOGRAPHY } from '../constants/interaxColors'

// Color definitions for the button gradients
// eslint-disable-next-line react-refresh/only-export-components
export const GLASSY_BUTTON_COLORS = {
  // Orange variant colors (Enter Team Lobby)
  orange: {
    outerGradient: 'linear-gradient(360deg, #FFAB40 0%, #FFBC66 100%)',
    innerGradient: 'linear-gradient(180deg, #FFAB40 0%, #FFBC66 100%)',
    name: 'orange' as const
  },
  // Gray variant colors (Game Tutorial)
  gray: {
    outerGradient: 'linear-gradient(360deg, #7C7C7C 0%, #CECECE 100%)',
    innerGradient: 'linear-gradient(180deg, #8F8F8F 0%, #CBCBCB 100%)',
    name: 'gray' as const
  }
} as const

export type GlassyButtonVariant = keyof typeof GLASSY_BUTTON_COLORS

const GLASSY_BUTTON_ICON_SIZE_PX = 28 // 28 px

// The size of the icon a glassy button carries.
// eslint-disable-next-line react-refresh/only-export-components
export const GLASSY_BUTTON_ICON_STYLE: React.CSSProperties = {
  width: GLASSY_BUTTON_ICON_SIZE_PX,
  height: GLASSY_BUTTON_ICON_SIZE_PX
}

export interface GlassyActionButtonProps {
  children: React.ReactNode
  onClick?: () => void
  icon?: React.ReactNode
  variant?: GlassyButtonVariant
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  fullWidth?: boolean
  'data-testid'?: string
}

/**
 * GlassyActionButton - A premium 3D embossed button with double-layer design
 *
 * Features:
 * - Double-layer design with outer padding frame and inner button
 * - Complex gradient backgrounds for depth
 * - Multiple inset shadows for 3D embossed effect
 * - High border radius for modern glassy appearance
 * - Icon support on the left side
 * - Orange and Gray color variants
 * - Responsive hover and active states
 *
 * Variants:
 * - orange: Perfect for primary actions like "Enter Team Lobby"
 * - gray: Perfect for secondary actions like "Game Tutorial"
 */
export const GlassyActionButton: React.FC<GlassyActionButtonProps> = ({
  children,
  onClick,
  icon,
  variant = 'orange',
  disabled = false,
  className = '',
  style = {},
  fullWidth = false,
  'data-testid': dataTestId
}) => {
  const colors = GLASSY_BUTTON_COLORS[variant]

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
    }
  }

  return (
    <Box
      className={className}
      onClick={handleClick}
      data-testid={dataTestId}
      sx={{
        // Outer frame with padding
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '11px',
        width: fullWidth ? '100%' : 'auto',
        minHeight: '96px',
        background: colors.outerGradient,
        boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25), inset 0px -2px 6.3px rgba(255, 255, 255, 0.5)',
        borderRadius: '30px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        userSelect: 'none',

        // Hover effects
        '&:hover': disabled
          ? {}
          : {
              transform: 'translateY(-2px)',
              filter: 'brightness(1.05)'
            },

        // Active/press effect
        '&:active': disabled
          ? {}
          : {
              transform: 'translateY(0px)',
              filter: 'brightness(0.95)'
            },

        // Focus styles for accessibility
        '&:focus-visible': {
          outline: `3px solid ${INTERAX_COLORS.sun}`,
          outlineOffset: '3px'
        }
      }}
      style={style}
    >
      {/* Inner button content */}
      <Box
        sx={{
          // Inner frame
          display: 'flex',
          flexDirection: 'row',
          justifyContent: icon ? 'flex-start' : 'center',
          alignItems: 'center',
          padding: '19px 16px',
          gap: '8px',
          width: '100%',
          flex: 1,
          background: colors.innerGradient,
          boxShadow:
            '0px -2px 4px rgba(255, 255, 255, 0.6), inset -2px 0px 4px rgba(0, 0, 0, 0.25), inset 2px 0px 4px rgba(0, 0, 0, 0.25), inset 0px 14px 15.9px rgba(0, 0, 0, 0.25)',
          borderRadius: '20px'
        }}
      >
        {/* Icon */}
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              color: INTERAX_COLORS.white,
              opacity: 0.8,
              filter: 'drop-shadow(0px 0px 3.34051px rgba(255, 255, 255, 0.75))',
              flexShrink: 0
            }}
          >
            {icon}
          </Box>
        )}

        {/* Text: smaller on a phone, and a label still too long for the button wraps instead of running past it */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            fontFamily: TYPOGRAPHY.headline3.fontFamily,
            fontWeight: TYPOGRAPHY.headline3.fontWeight,
            fontSize: {
              xs: TYPOGRAPHY.headline4.fontSize,
              sm: TYPOGRAPHY.headline3.fontSize,
              md: TYPOGRAPHY.headline3.fontSize
            },
            lineHeight: {
              xs: TYPOGRAPHY.headline4.lineHeight,
              sm: TYPOGRAPHY.headline3.lineHeight,
              md: TYPOGRAPHY.headline3.lineHeight
            },
            color: INTERAX_COLORS.white,
            opacity: 0.8,
            textShadow: '0px 0px 3.5px rgba(255, 255, 255, 0.75)',
            whiteSpace: { xs: 'normal', sm: 'nowrap', md: 'nowrap' },
            overflowWrap: 'anywhere'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default GlassyActionButton
